/* eslint-disable react/jsx-props-no-spreading, react/require-default-props */
import React, { useContext } from 'react';
import proptypes from 'prop-types';
import {
  params, string, arrayOf, extend, shape, bool, number, QueryContext
} from '@thd-nucleus/data-sources';

import { useBundleProducts } from '../hooks/useBundleProducts';
import { useBundlePricingDetails } from '../hooks/useBundlePricingDetails';

const { string: propTypeString, bool: propTypeBool } = proptypes;

const dataModel = extend(
  {
    product: params({ itemId: string().isRequired() }).shape({
      itemId: string(),
      bundleSpecificationDetails: params({ storeId: string(), zipCode: string() }).shape({
        type: string(),
        brand: string(),
        experienceType: string(),
        accessories: shape({
          categories: arrayOf(shape({
            categoryName: string(),
            offerings: arrayOf(shape({
              offeringName: string(),
              products: arrayOf(shape({
                id: string(),
                defaultProductId: string(),
                type: string(),
                quantity: string()
              }))
            }))
          }))
        }),
        components: arrayOf(shape({
          id: string(),
          defaultProductId: string(),
          quantity: string(),
          type: string(),
          allowSwap: bool(),
          allowRemove: bool(),
          selectableVariants: arrayOf(shape({
            name: string(),
            id: string(),
            guid: string(),
            value: string()
          })),
          priority: number()
        }))
      })
    })
  },
  {
    products: params({ itemIds: arrayOf(string().isRequired()).isRequired(), filterResponse: bool() }).arrayOf({
      itemId: string(),
      identifiers: shape({
        brandName: string(),
        productLabel: string(),
        productType: string(),
        hideItem: string()
      }),
      availabilityType: shape({
        obsolete: bool(),
        discontinued: bool()
      }),
      info: shape({
        isSponsored: bool(),
        minimumOrderQuantity: number()
      })
    })
  },
  {
    checkAvailability: params({
      itemIds: arrayOf(string().isRequired()).isRequired(),
      zipCode: string().isRequired(),
      localStoreId: string().isRequired()
    })
      .shape({
        fulfillmentOptions: arrayOf(shape({
          services: arrayOf(shape({
            deliveryAvailabilityDetail: arrayOf(shape({
              itemId: string(),
              status: string(),
              assortmentStrategy: string()
            }))
          }))
        }))
      })
  }
);

export const withBundleProducts = (BaseComponent) => {

  const BundleComponent = ({ bundleId, storeId, listenToProductsChange = true, ...rest }) => {
    const { defaultVariables } = useContext(QueryContext) || {};
    const isCustomerIdentified = defaultVariables?.current?.isBrandPricingPolicyCompliant || false;
    const bundleProducts = useBundleProducts(bundleId, storeId, listenToProductsChange, isCustomerIdentified);
    const pricingDetails = useBundlePricingDetails({ ...bundleProducts, isCustomerIdentified });
    return (
      <BaseComponent
        {...bundleProducts}
        {...rest}
        pricingDetails={pricingDetails}
      />
    );
  };

  BundleComponent.dataModel = BaseComponent?.dataModel
    ? extend(dataModel, BaseComponent?.dataModel)
    : dataModel;

  BundleComponent.propTypes = {
    bundleId: propTypeString.isRequired,
    storeId: propTypeString.isRequired,
    listenToProductsChange: propTypeBool
  };

  return BundleComponent;
};